
// Localization utilities for ProductVideos page

type ProcessStep = {
  title: string;
  description: string;
};

type UseCase = {
  title: string;
  description: string;
};

type FAQ = {
  question: string;
  answer: string;
};

export type LocalizedContent = {
  heroSubtitle: string;
  productShowcaseTitle: string;
  productShowcaseText: string;
  benefitsTitle: string;
  benefitPoints: string[];
  processTitle: string;
  processSteps: ProcessStep[];
  useCasesTitle: string;
  useCases: UseCase[];
  faqTitle: string;
  faqs: FAQ[];
  ctaTitle: string;
  ctaText: string;
};

// Multilingual content based on language
const localizedContent: Record<string, LocalizedContent> = {
  de: {
    heroSubtitle: "Unsere KI-gestützten Produktvideos präsentieren Ihre Produkte im besten Licht – automatisch generiert, schnell produziert und überzeugend gestaltet.",
    productShowcaseTitle: "Produktvideos für Ihre Marke",
    productShowcaseText: "Unsere KI-gestützten Produktvideos präsentieren Ihre Produkte im besten Licht – automatisch generiert, schnell produziert und überzeugend gestaltet.",
    benefitsTitle: "Vorteile von KI-Produktvideos",
    benefitPoints: [
      "Hochwertige Produktpräsentationen",
      "Automatisierte Erstellung basierend auf Ihren Produktdaten",
      "Konsistentes Branding über alle Videos hinweg",
      "Erhöhte Conversion-Raten in Ihrem Online-Shop"
    ],
    processTitle: "So funktioniert es",
    processSteps: [
      {
        title: "Daten liefern",
        description: "Teilen Sie uns Ihre Produktdaten, Brand-Assets und Zielgruppen-Informationen mit."
      },
      {
        title: "KI-Generierung",
        description: "Unsere Technologie erstellt automatisch ansprechende Produktvideos in Ihrem Branding."
      },
      {
        title: "Feedback & Anpassung",
        description: "Sie geben Feedback, wir nehmen Anpassungen vor, bis Sie vollkommen zufrieden sind."
      },
      {
        title: "Veröffentlichung",
        description: "Integrieren Sie die fertigen Videos in Ihren Online-Shop, Websites und Social-Media-Kanäle."
      }
    ],
    useCasesTitle: "Einsatzbereiche für KI-Produktvideos",
    useCases: [
      {
        title: "E-Commerce Shops",
        description: "Steigern Sie Ihre Conversion-Rate durch überzeugende Produktvideos direkt auf den Produktseiten."
      },
      {
        title: "Social Media Werbung",
        description: "Nutzen Sie dynamische Produktvideos für höhere Engagement-Raten in sozialen Medien."
      },
      {
        title: "Email-Marketing",
        description: "Heben Sie sich im Posteingang ab mit animierten Produktvorstellungen direkt in der Email."
      },
      {
        title: "Messeauftritte & Präsentationen",
        description: "Setzen Sie professionelle Produktvideos bei Messen und Kundenpräsentationen ein."
      }
    ],
    faqTitle: "Häufig gestellte Fragen",
    faqs: [
      {
        question: "Wie lange dauert die Produktion eines KI-Produktvideos?",
        answer: "In der Regel beträgt die Lieferzeit 3-5 Werktage ab Erhalt aller notwendigen Informationen und Assets."
      },
      {
        question: "Benötige ich professionelle Produktfotos für KI-Produktvideos?",
        answer: "Hochwertige Produktfotos sind ideal, aber unsere KI kann auch mit standard Produktbildern arbeiten und diese optimieren."
      },
      {
        question: "Kann ich die Videos für alle Produkte in meinem Sortiment erstellen lassen?",
        answer: "Ja, unsere Technologie ist speziell für die Skalierung konzipiert. Wir können Hunderte von Produktvideos mit konsistentem Branding erstellen."
      },
      {
        question: "Welche Formate und Auflösungen bieten Sie an?",
        answer: "Wir liefern Videos in allen gängigen Formaten (16:9, 9:16, 1:1) und Auflösungen bis 4K, optimiert für verschiedene Plattformen."
      }
    ],
    ctaTitle: "Bereit für überzeugende Produktvideos?",
    ctaText: "Lassen Sie uns gemeinsam besprechen, wie wir Ihre Produkte mit KI-generierten Videos zum Leben erwecken können."
  },
  en: {
    heroSubtitle: "Our AI-powered product videos showcase your products in the best light - automatically generated, quickly produced and convincingly designed.",
    productShowcaseTitle: "Product Videos for Your Brand",
    productShowcaseText: "Our AI-powered product videos showcase your products in the best light - automatically generated, quickly produced, and convincingly designed.",
    benefitsTitle: "Benefits of AI Product Videos",
    benefitPoints: [
      "High-quality product presentations",
      "Automated creation based on your product data",
      "Consistent branding across all videos",
      "Increased conversion rates in your online shop"
    ],
    processTitle: "How It Works",
    processSteps: [
      {
        title: "Provide Data",
        description: "Share your product data, brand assets, and target audience information with us."
      },
      {
        title: "AI Generation",
        description: "Our technology automatically creates appealing product videos in your branding."
      },
      {
        title: "Feedback & Adjustment",
        description: "You provide feedback, we make adjustments until you're completely satisfied."
      },
      {
        title: "Publication",
        description: "Integrate the finished videos into your online shop, websites, and social media channels."
      }
    ],
    useCasesTitle: "Use Cases for AI Product Videos",
    useCases: [
      {
        title: "E-Commerce Shops",
        description: "Increase your conversion rate with compelling product videos directly on product pages."
      },
      {
        title: "Social Media Advertising",
        description: "Use dynamic product videos for higher engagement rates on social media."
      },
      {
        title: "Email Marketing",
        description: "Stand out in the inbox with animated product presentations directly in the email."
      },
      {
        title: "Trade Shows & Presentations",
        description: "Use professional product videos at trade shows and customer presentations."
      }
    ],
    faqTitle: "Frequently Asked Questions",
    faqs: [
      {
        question: "How long does it take to produce an AI product video?",
        answer: "Typically, delivery time is 3-5 business days from receipt of all necessary information and assets."
      },
      {
        question: "Do I need professional product photos for AI product videos?",
        answer: "High-quality product photos are ideal, but our AI can also work with standard product images and optimize them."
      },
      {
        question: "Can I have videos created for all products in my range?",
        answer: "Yes, our technology is specifically designed for scaling. We can create hundreds of product videos with consistent branding."
      },
      {
        question: "What formats and resolutions do you offer?",
        answer: "We deliver videos in all common formats (16:9, 9:16, 1:1) and resolutions up to 4K, optimized for different platforms."
      }
    ],
    ctaTitle: "Ready for compelling product videos?",
    ctaText: "Let's discuss how we can bring your products to life with AI-generated videos."
  },
  fr: {
    heroSubtitle: "Nos vidéos de produits basées sur l'IA présentent vos produits sous leur meilleur jour - générées automatiquement, produites rapidement et conçues de manière convaincante.",
    productShowcaseTitle: "Vidéos de Produits pour Votre Marque",
    productShowcaseText: "Nos vidéos de produits basées sur l'IA présentent vos produits sous leur meilleur jour - générées automatiquement, produites rapidement et conçues de manière convaincante.",
    benefitsTitle: "Avantages des Vidéos de Produits IA",
    benefitPoints: [
      "Présentations de produits de haute qualité",
      "Création automatisée basée sur vos données de produits",
      "Image de marque cohérente sur toutes les vidéos",
      "Taux de conversion accrus dans votre boutique en ligne"
    ],
    processTitle: "Comment Ça Fonctionne",
    processSteps: [
      {
        title: "Fournir les Données",
        description: "Partagez vos données de produits, éléments de marque et informations sur le public cible."
      },
      {
        title: "Génération par IA",
        description: "Notre technologie crée automatiquement des vidéos de produits attrayantes à votre image."
      },
      {
        title: "Retour et Ajustement",
        description: "Vous fournissez des commentaires, nous apportons des ajustements jusqu'à votre entière satisfaction."
      },
      {
        title: "Publication",
        description: "Intégrez les vidéos finalisées dans votre boutique en ligne, sites web et réseaux sociaux."
      }
    ],
    useCasesTitle: "Cas d'Utilisation pour les Vidéos de Produits IA",
    useCases: [
      {
        title: "Boutiques E-Commerce",
        description: "Augmentez votre taux de conversion avec des vidéos de produits convaincantes directement sur les pages de produits."
      },
      {
        title: "Publicité sur Réseaux Sociaux",
        description: "Utilisez des vidéos de produits dynamiques pour des taux d'engagement plus élevés sur les réseaux sociaux."
      },
      {
        title: "Marketing par E-mail",
        description: "Démarquez-vous dans la boîte de réception avec des présentations de produits animées directement dans l'e-mail."
      },
      {
        title: "Salons Professionnels & Présentations",
        description: "Utilisez des vidéos de produits professionnelles lors de salons et présentations clients."
      }
    ],
    faqTitle: "Questions Fréquemment Posées",
    faqs: [
      {
        question: "Combien de temps faut-il pour produire une vidéo de produit IA?",
        answer: "Généralement, le délai de livraison est de 3 à 5 jours ouvrables à compter de la réception de toutes les informations et éléments nécessaires."
      },
      {
        question: "Ai-je besoin de photos de produits professionnelles pour les vidéos de produits IA?",
        answer: "Des photos de produits de haute qualité sont idéales, mais notre IA peut également travailler avec des images de produits standard et les optimiser."
      },
      {
        question: "Puis-je faire créer des vidéos pour tous les produits de ma gamme?",
        answer: "Oui, notre technologie est spécialement conçue pour l'évolutivité. Nous pouvons créer des centaines de vidéos de produits avec une image de marque cohérente."
      },
      {
        question: "Quels formats et résolutions proposez-vous?",
        answer: "Nous livrons des vidéos dans tous les formats courants (16:9, 9:16, 1:1) et des résolutions jusqu'à 4K, optimisées pour différentes plateformes."
      }
    ],
    ctaTitle: "Prêt pour des vidéos de produits convaincantes?",
    ctaText: "Discutons de la façon dont nous pouvons donner vie à vos produits avec des vidéos générées par IA."
  },
  it: {
    heroSubtitle: "I nostri video di prodotti basati sull'IA mostrano i tuoi prodotti nella migliore luce - generati automaticamente, prodotti rapidamente e progettati in modo convincente.",
    productShowcaseTitle: "Video di Prodotti per il Tuo Brand",
    productShowcaseText: "I nostri video di prodotti basati sull'IA mostrano i tuoi prodotti nella migliore luce - generati automaticamente, prodotti rapidamente e progettati in modo convincente.",
    benefitsTitle: "Vantaggi dei Video di Prodotti IA",
    benefitPoints: [
      "Presentazioni di prodotti di alta qualità",
      "Creazione automatizzata basata sui dati dei tuoi prodotti",
      "Branding coerente su tutti i video",
      "Tassi di conversione aumentati nel tuo negozio online"
    ],
    processTitle: "Come Funziona",
    processSteps: [
      {
        title: "Fornire i Dati",
        description: "Condividi i dati dei tuoi prodotti, asset del brand e informazioni sul pubblico di riferimento."
      },
      {
        title: "Generazione IA",
        description: "La nostra tecnologia crea automaticamente accattivanti video di prodotti con il tuo branding."
      },
      {
        title: "Feedback e Modifiche",
        description: "Tu fornisci feedback, noi apportiamo modifiche fino a quando non sei completamente soddisfatto."
      },
      {
        title: "Pubblicazione",
        description: "Integra i video finiti nel tuo negozio online, siti web e canali social media."
      }
    ],
    useCasesTitle: "Casi d'Uso per Video di Prodotti IA",
    useCases: [
      {
        title: "Negozi E-Commerce",
        description: "Aumenta il tuo tasso di conversione con video di prodotti convincenti direttamente sulle pagine dei prodotti."
      },
      {
        title: "Pubblicità sui Social Media",
        description: "Utilizza video di prodotti dinamici per tassi di coinvolgimento più elevati sui social media."
      },
      {
        title: "Email Marketing",
        description: "Distinguiti nella casella di posta con presentazioni animate dei prodotti direttamente nell'email."
      },
      {
        title: "Fiere e Presentazioni",
        description: "Utilizza video di prodotti professionali in fiere e presentazioni ai clienti."
      }
    ],
    faqTitle: "Domande Frequenti",
    faqs: [
      {
        question: "Quanto tempo ci vuole per produrre un video di prodotto IA?",
        answer: "In genere, il tempo di consegna è di 3-5 giorni lavorativi dal ricevimento di tutte le informazioni e gli asset necessari."
      },
      {
        question: "Ho bisogno di foto di prodotti professionali per i video di prodotti IA?",
        answer: "Le foto di prodotti di alta qualità sono ideali, ma la nostra IA può anche lavorare con immagini di prodotti standard e ottimizzarle."
      },
      {
        question: "Posso far creare video per tutti i prodotti della mia gamma?",
        answer: "Sì, la nostra tecnologia è specificamente progettata per la scalabilità. Possiamo creare centinaia di video di prodotti con un branding coerente."
      },
      {
        question: "Quali formati e risoluzioni offrite?",
        answer: "Forniamo video in tutti i formati comuni (16:9, 9:16, 1:1) e risoluzioni fino a 4K, ottimizzati per diverse piattaforme."
      }
    ],
    ctaTitle: "Pronto per video di prodotti convincenti?",
    ctaText: "Discutiamo di come possiamo dare vita ai tuoi prodotti con video generati dall'IA."
  }
};

export const getLocalizedContent = (language: string): LocalizedContent => {
  return localizedContent[language] || localizedContent.en;
};
